const { Core, Utils, Events } = Global;

customElements.define('cart-provider', class extends Core {
    liveSections = new Set();
    notificationsEnabled = false;
    pendingAdds = new Set();
    requestQueue = Promise.resolve();

    render() {
        this.sub(Events.CART_REGISTER, this._registerSection, { global: true });
        this.sub(Events.CART_NOTIFICATIONS_ENABLING, this._enableNotifications, { global: true });
        this.sub(Events.CART_ADD, this._addToCart, { global: true });
        this.sub(Events.CART_CHANGE, this._updateCart, { global: true });
        this.sub(Events.CART_REPLACE, this._replaceCartItem, { global: true });
        this.sub(Events.PRODUCT_FORM_SUBMIT, this._formSubmitHandler, { global: true });
        this.refreshHandler = this._cartRefresh.bind(this);
        document.addEventListener('cart:refresh', this.refreshHandler);
    }

    destroy() {
        document.removeEventListener('cart:refresh', this.refreshHandler);
    }

    _registerSection(section) {
        if (section) this.liveSections.add(section);
    }

    _enableNotifications() {
        this.notificationsEnabled = true;
    }

    _sectionIds() {
        return Array.from(new Set(Array.from(document.querySelectorAll('cart-live-region'))
            .map(region => region.getAttribute('target-section') || region.getAttribute('section-id'))
            .filter(Boolean)));
    }

    _enqueue(callback, signature) {
        if (signature && this.pendingAdds.has(signature)) return this.requestQueue;
        if (signature) this.pendingAdds.add(signature);
        const task = this.requestQueue.then(callback);
        this.requestQueue = task.catch(error => {
            console.error(error);
            this.pub(Events.CART_ERROR, error);
        }).finally(() => {
            if (signature) this.pendingAdds.delete(signature);
        });
        return this.requestQueue;
    }

    _formSubmitHandler({ formData }) {
        const signature = JSON.stringify(Array.from(formData.entries()).map(([key, value]) =>
            [key, typeof value === 'string' ? value : `${value.name}:${value.size}`]));
        return this._enqueue(() => this._requestCart('cartAdd', formData), signature);
    }

    _addToCart({ items }) {
        return this._enqueue(() => this._requestCart('cartAdd', { items }), JSON.stringify(items));
    }

    _fetchCart(route, body) {
        const isForm = body instanceof FormData;
        return fetch(`${window.dynamicURLs[route]}.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                ...(isForm ? {} : { 'Content-Type': 'application/json' })
            },
            body: isForm ? body : JSON.stringify(body)
        });
    }

    async _readResponse(response) {
        const data = await response.json();
        if (!response.ok) {
            const error = new Error(data.description || data.message || window.cartStrings.error);
            error.confirmedFailure = response.status >= 400 && response.status < 500;
            if (!document.querySelector('cart-notification')) {
                this.pub(Events.TOAST_NOTIFICATION, {
                    type: 'error', title: data.message, msg: error.message, autoclose: false
                });
            }
            throw error;
        }
        return data;
    }

    _updateCart({ quantity, key, line }) {
        return this._enqueue(() => this._requestCart('cartChange', {
            ...(key ? { id: key } : { line }), quantity: Number(quantity)
        }));
    }

    _replaceCartItem({ key, newItem, quantity, properties, sellingPlan }) {
        return this._enqueue(async () => {
            let added = false;
            try {
                if (!key) throw new Error(window.cartStrings.error);
                if (String(newItem) === String(key).split(':')[0]) {
                    await this._publishCartUpdate({}, undefined);
                    return;
                }
                await this._readResponse(await this._fetchCart('cartAdd', {
                    items: [{ id: newItem, quantity: Number(quantity), ...(properties && { properties }), ...(sellingPlan && { selling_plan: sellingPlan }) }]
                }));
                added = true;
                const removed = await this._requestCart('cartChange', { id: key, quantity: 0 });
                if (!removed) window.location.assign(window.dynamicURLs.cart);
            } catch (error) {
                if (added) window.location.assign(window.dynamicURLs.cart);
                throw error;
            }
        });
    }

    async _requestCart(route, body) {
        const sectionIds = this._sectionIds();
        const sectionContext = window.location.pathname;
        const isForm = body instanceof FormData;
        if (isForm) {
            body.set('sections', sectionIds.slice(0, 5).join(','));
            body.set('sections_url', sectionContext);
            if (this.notificationsEnabled) {
                body.set('attributes[notification_items_variant_ids]', body.get('id'));
            }
        } else {
            body = { ...body, sections: sectionIds.slice(0, 5), sections_url: sectionContext };
            if (route === 'cartAdd' && this.notificationsEnabled) {
                body.attributes = { notification_items_variant_ids: body.items.map(item => item.id).join(',') };
            }
        }
        let cartData;
        try {
            cartData = await this._readResponse(await this._fetchCart(route, body));
        } catch (error) {
            this.pub(Events.CART_ERROR, error);
            if (!error.confirmedFailure && !document.querySelector('cart-notification')) {
                window.location.assign(window.dynamicURLs.cart);
            }
            return false;
        }
        const addedItems = route === 'cartAdd' ? (cartData.items || [cartData]) : [];
        await this._publishCartUpdate(cartData.sections, route, sectionIds, addedItems, cartData);
        if (route === 'cartAdd' && this.notificationsEnabled) {
            try {
                const response = await this._fetchCart('cartUpdate', { attributes: { notification_items_variant_ids: '' } });
                if (!response.ok) console.error('Cart notification attribute cleanup failed');
            } catch (error) {
                console.error(error);
            }
        }
        return true;
    }

    _validSection(sectionId, html, addedItems = [], cartData = {}) {
        if (typeof html !== 'string' || !html.trim()) return false;
        const doc = Utils.parseHTML(html);
        const preview = doc.querySelector('[data-added-variant-id]');
        if (addedItems.length && doc.getElementById('cartNotificationPopupLive') && !preview) return false;
        if (preview && addedItems.length) {
            const added = addedItems.find(item => item.key
                ? item.key === preview.getAttribute('data-added-item-key')
                : String(item.variant_id || item.id) === preview.getAttribute('data-added-variant-id'));
            if (!added || (preview.hasAttribute('data-added-quantity') && Number(preview.getAttribute('data-added-quantity')) !== added.quantity)) return false;
        }
        const total = doc.querySelector('[data-cart-item-count]');
        if (total && typeof cartData.item_count === 'number' && (
            Number(total.getAttribute('data-cart-item-count')) !== cartData.item_count ||
            Number(total.getAttribute('data-cart-total-price')) !== cartData.total_price
        )) return false;
        return Array.from(document.querySelectorAll('cart-live-region')).filter(region =>
            (region.getAttribute('target-section') || region.getAttribute('section-id')) === sectionId
        ).every(region => {
            const selector = region.getAttribute('target-element');
            return selector ? doc.querySelector(selector) : doc.getElementById(region.id);
        });
    }

    async _fetchSections(ids, addedItems, cartData) {
        const sections = {};
        for (let start = 0; start < ids.length; start += 5) {
            const url = new URL(window.location.href);
            url.searchParams.delete('section_id');
            url.searchParams.set('sections', ids.slice(start, start + 5).join(','));
            url.searchParams.set('_cart_refresh', Date.now().toString());
            const response = await fetch(url.toString(), {
                credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
            });
            if (!response.ok) throw new Error('Cart section refresh failed');
            Object.assign(sections, await response.json());
        }
        const unresolved = ids.filter(id => !this._validSection(id, sections[id], addedItems, cartData));
        if (unresolved.length) {
            const url = new URL(window.location.href);
            url.searchParams.delete('sections');
            url.searchParams.delete('section_id');
            url.searchParams.set('_cart_refresh', Date.now().toString());
            const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
            if (!response.ok) throw new Error('Cart page refresh failed');
            const doc = Utils.parseHTML(await response.text());
            unresolved.forEach(id => {
                const section = doc.getElementById(`shopify-section-${id}`);
                if (section) sections[id] = section.outerHTML;
            });
        }
        return sections;
    }

    async _publishCartUpdate(initialSections, req, sectionIds = this._sectionIds(), addedItems = [], cartData = {}) {
        const sections = { ...initialSections };
        const missing = sectionIds.filter(id => !this._validSection(id, sections[id], addedItems, cartData));
        try {
            if (missing.length) Object.assign(sections, await this._fetchSections(missing, addedItems, cartData));
        } catch (error) {
            console.error(error);
        }
        const update = {
            sections, req, addedItems,
            renderFailed: sectionIds.some(id => !this._validSection(id, sections[id], addedItems, cartData))
        };
        try {
            this.pub(Events.CART_UPDATE, update);
        } catch (error) {
            update.renderFailed = true;
            console.error(error);
            window.location.assign(window.dynamicURLs.cart);
        }
        if (update.renderFailed && (req !== 'cartAdd' || !document.querySelector('cart-notification'))) {
            window.location.assign(window.dynamicURLs.cart);
        }
    }

    _cartRefresh() {
        return this._enqueue(async () => {
            await this._publishCartUpdate({}, undefined);
        });
    }
});

customElements.define('cart-qty', class extends Core {
    propTypes = {
        value: Number,
        key: String,
        input: Boolean,
        'prevent-default': Boolean,
        line: Number
    }

    render() {
        this.$({
            [this.evt]: this._handleEvent
        })
    }

    _handleEvent(e) {
        this.prop('prevent-default') && e.preventDefault();
        if (this.hasAttribute('disabled') || this.getAttribute('aria-disabled') === 'true') return;
        this.pub(Events.CART_CHANGE, {
            key: this.prop('key'),
            line: this.prop('line'),
            quantity: this._getValue(e)
        })
    }

    _getValue(e) {
        return this.evt === 'change'
            ? this._getValidatedValue(e)
            : this.prop('value'); 
    }

    _getValidatedValue(e) {
        const maxValue = e.target.hasAttribute('max') && +e.target.getAttribute('max');
        if (maxValue && e.target.value > maxValue) {
            e.target.value = maxValue
            return maxValue
        }
        const value = Number(e.target.value);
        if (e.target.value.trim() === '' || !Number.isInteger(value) || value < 0) {
            e.target.value = e.target.defaultValue;
            return Number(e.target.defaultValue);
        }
        return value
    }

    get evt() {
        return this.prop('input') 
            ? 'change' 
            : 'click';
    }
});

customElements.define('cart-live-region', class extends Core {
    propTypes = {
        'target-section': String,
        'target-element': String
    }

    render() {
        this.targetSection = this.prop('target-section') || this.sectionId;
        this.targetSelector = this.prop('target-element');
        customElements.whenDefined('cart-provider').then(() => {
            this.pub(Events.CART_REGISTER, this.targetSection);
        });
        this.sub(Events.CART_UPDATE, this._updateContent, { global: true });
    }

    _updateContent(update) {
        const html = update.sections && update.sections[this.targetSection];
        if (typeof html !== 'string') {
            update.renderFailed = true;
            return;
        }
        try {
            const doc = Utils.parseHTML(html);
            const content = this.targetSelector ? doc.querySelector(this.targetSelector) : doc.getElementById(this.id);
            if (!content) {
                update.renderFailed = true;
                return;
            }
            Utils.$replaceContent(this, content);
        } catch (error) {
            update.renderFailed = true;
            console.error(error);
        }
    }
});
